import React, { lazy } from "react";
import { withLazyRoute } from "@/components/loading/LazyRouteWrapper";
import {
  Home as HomeIcon,
  ViewKanban as ViewKanbanIcon,
  BorderColor as BorderColorIcon,
  VideoLibrary as VideoLibraryIcon,
  Handyman as HandymanIcon,
} from "@mui/icons-material";
import {
  Navigation,
  NavigationItem,
  NavigationPageItem,
} from "types/navigation";
// import {
//   FreeloHome,
//   FreeloBoard,
//   MyMeetingsView,
//   SharedMeetingDetails,
// } from "@/views/Shared";
const FreeloHome = withLazyRoute(
  lazy(() =>
    import("views/Shared/FreeloHome").then((m) => ({ default: m.FreeloHome }))
  ),
  "Loading freelo..."
);
const FreeloBoard = withLazyRoute(
  lazy(() => import("views/Shared/FreeloBoard")),
  "Loading freelo board..."
);

const MyMeetingsView = withLazyRoute(
  lazy(() =>
    import("views/Shared/MyMeetings/MyMeetingsView").then((m) => ({
      default: m.MyMeetingsView,
    }))
  ),
  "Loading meetings view..."
);
const SharedMeetingDetails = withLazyRoute(
  lazy(() =>
    import(
      "views/Shared/MyMeetings/SharedMeetingDetails/SharedMeetingDetails"
    ).then((m) => ({ default: m.SharedMeetingDetails }))
  ),
  "Loading meeting details..."
);
import { StakeholderInvoiceCount } from "@/components/ui/Icons";
import { StakeholderPermissionClaims } from "@freetech/models/user";

const StakeholderHome = withLazyRoute(
  lazy(() =>
    import("@/views/Dashboards/Stakeholder")
  ),
  "Loading stakeholder home..."
);
const StakeholderPayments = withLazyRoute(
  lazy(() =>
    import("views/Stakeholder/StakeholderPayments/StakeholderPayments").then(
      (m) => ({ default: m.StakeholderPayments })
    )
  ),
  "Loading payments..."
);
const StakeholderProjectHub = withLazyRoute(
  lazy(() =>
    import(
      "views/Stakeholder/StakeholderProjectHub/StakeholderProjectHub"
    ).then((m) => ({ default: m.StakeholderProjectHub }))
  ),
  "Loading project hub..."
);
const StakeholderContracts = withLazyRoute(
  lazy(
    () => import("views/Stakeholder/StakeholderContracts/StakeholderContracts")
  ),
  "Loading contracts..."
);
const StakeholderProjectOverview = withLazyRoute(
  lazy(
    () =>
      import(
        "views/Stakeholder/StakeholderProjectHub/StakeholderProjectOverview"
      )
  ),
  "Loading project overview..."
);

interface StakeholderPagePermissionRequirement {
  category: keyof StakeholderPermissionClaims;
  action: string;
  value?: any; // For specific permission values
}

const STAKEHOLDER_PAGE_PERMISSIONS: Record<
  string,
  StakeholderPagePermissionRequirement | null
> = {
  stakeholder: null, // Home page - always accessible to stakeholders
  "stakeholder/projects": {
    category: "projects",
    action: "viewer",
  },
  "stakeholder/my-meetings": {
    category: "meetings",
    action: "view",
  },
  freelo: {
    category: "freelo",
    action: "view",
  },
  "stakeholder/contracts": {
    category: "contracts",
    action: "view",
  },
  "stakeholder/bill-pay": {
    category: "billing",
    action: "view",
  },
};

/**
 * Check if user has permission for specific stakeholder page
 */
function hasStakeholderPagePermission(
  permissions: StakeholderPermissionClaims | null,
  segment: string
): boolean {
  if (!permissions) {
    return false;
  }

  const requirement = STAKEHOLDER_PAGE_PERMISSIONS[segment];
  if (!requirement) {
    return true; // No requirement means accessible
  }

  const categoryPerms = permissions[requirement.category];
  if (!categoryPerms) {
    return false;
  }

  const permissionValue = (categoryPerms as any)[requirement.action];

  if (typeof permissionValue === "boolean") {
    return permissionValue;
  } else if (typeof permissionValue === "string") {
    return permissionValue !== "none" && permissionValue !== "";
  } else if (Array.isArray(permissionValue)) {
    return permissionValue.length > 0;
  }

  return false;
}

const filterStakeholderNavigationByPermissions = (
  items: Navigation,
  permissions: StakeholderPermissionClaims | null
): Navigation => {
  const filteredItems: Navigation = [];

  for (const item of items) {
    if (item.kind === "header" || item.kind === "divider") {
      filteredItems.push(item);
    } else {
      const pageItem = item as NavigationPageItem;

      if (
        pageItem.segment &&
        hasStakeholderPagePermission(permissions, pageItem.segment)
      ) {
        filteredItems.push(item);
      } else if (pageItem.children) {
        const filteredChildren = filterStakeholderNavigationByPermissions(
          pageItem.children,
          permissions
        );
        if (filteredChildren.length > 0) {
          filteredItems.push({
            ...pageItem,
            children: filteredChildren,
          });
        }
      }
    }
  }

  const cleanedItems: Navigation = [];
  let pendingHeader: NavigationItem | null = null;
  let pendingDivider: NavigationItem | null = null;
  let hasPagesSinceLastHeader = false;

  for (let i = 0; i < filteredItems.length; i++) {
    const item = filteredItems[i];

    if (item.kind === "header") {
      if (pendingHeader && hasPagesSinceLastHeader) {
        cleanedItems.push(pendingHeader);
        if (pendingDivider) {
          cleanedItems.push(pendingDivider);
        }
      }

      pendingHeader = item;
      pendingDivider = null;
      hasPagesSinceLastHeader = false;
    } else if (item.kind === "divider") {
      pendingDivider = item;
    } else {
      if (pendingHeader && !hasPagesSinceLastHeader) {
        cleanedItems.push(pendingHeader);
        if (pendingDivider) {
          cleanedItems.push(pendingDivider);
        }
        pendingHeader = null;
        pendingDivider = null;
      }

      cleanedItems.push(item);
      hasPagesSinceLastHeader = true;
    }
  }

  return cleanedItems;
};

export const stakeholderPages: Navigation = [
  {
    segment: "stakeholder",
    title: "Home",
    icon: <HomeIcon />,
    component: <StakeholderHome />,
  },
  {
    kind: "page",
    title: "Freelo",
    segment: "freelo",
    icon: <ViewKanbanIcon />,
    component: <FreeloHome />,
    slugBabyPattern: ":boardId",
    slugBabyComponent: <FreeloBoard />,
  },
  {
    segment: "stakeholder/my-meetings",
    title: "My Meetings",
    icon: <VideoLibraryIcon />,
    component: <MyMeetingsView />,
    slugBabyPattern: ":meetingId",
    slugBabyComponent: <SharedMeetingDetails />,
  },

  {
    segment: "stakeholder/projects",
    title: "Project Hub",
    icon: <HandymanIcon />,
    component: <StakeholderProjectHub />,
    slugBabyPattern: ":projectId",
    slugBabyComponent: <StakeholderProjectOverview />,
  },

  {
    segment: "stakeholder/contracts",
    title: "Contract Center",

    icon: <BorderColorIcon />,
    component: <StakeholderContracts />,
  },
  {
    segment: "stakeholder/bill-pay",
    title: "Bill Pay",
    icon: <StakeholderInvoiceCount />,
    component: <StakeholderPayments />,
  },
];

/**
 * Get stakeholder pages filtered by user permissions
 * @param permissions - User's stakeholder permissions
 * @returns Filtered navigation array based on permissions
 */
export const getStakeholderPages = (
  permissions: StakeholderPermissionClaims
): Navigation => {
  return filterStakeholderNavigationByPermissions(
    stakeholderPages,
    permissions
  );
};
